import type { EmpresaResponse } from "@/types";
import type { 
  UserInvitationResponse, 
  UserInvitationListResponse, 
  UserInvitationRequest,
 
  UserInvitation
} from "@/types/user-invitation.model";
import type { UsersPermissionsUser } from "@/types/user.model";
import ApiClient from "../api/axios/apiClient";
import ApiClientOpen from "../api/axios/apiClientOpen";
import { useAuthStore } from "@/stores/auth.store";

/**
 * Links an empresa to a user
 */
export const linkEmpresaAUsuario = async (
  empresaDocumentId: string,
  userId: number
): Promise<EmpresaResponse> => {
  try {
    const response = await ApiClientOpen.post(`empresa-usuarios`,
      {data: {  usuario: userId, empresa: empresaDocumentId  }}
    );
    return response.data;
  } catch (error) {
    console.error('Error linking empresa to user:', error);
    throw error;
  }
}

/**
 * Invites a user to join an empresa
 * @param email - The email of the user to invite
 * @param nombre - The name of the user to invite
 * @param empresaId - The ID of the empresa to invite the user to
 * @returns Promise containing the created invitation
 */
export const inviteUserToEmpresa = async (
    email: string,
    nombre: string,
    role: string,
    empresaId?: string
): Promise<UserInvitationResponse> => {
  try {
    if (!empresaId) {
      throw new Error('Empresa ID is required to invite a user.');
    }
    const invitationData: UserInvitationRequest = {
        email,
        nombre,
        role,
        estado: 'pending',
        tenantId: empresaId
    };

    const response = await ApiClient.post('/user-invitations/send-invitation', invitationData);
    return response.data;
  } catch (error) {
    console.error('Error inviting user to empresa:', error);
    throw error;
  }
};

/**
 * Lists all invitations sent by an empresa
 * @param empresaId - The ID of the empresa
 * @param page - Optional page number for pagination
 * @param pageSize - Optional page size for pagination
 * @returns Promise containing the list of invitations
 */
export const listEmpresaInvitations = async (
  page: number = 1,
  pageSize: number = 10
): Promise<UserInvitationListResponse> => {
  try {
    const response = await ApiClient.get('/user-invitations', {
      params: {
        populate: '*',
        pagination: {
          page,
          pageSize
        }
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error listing empresa invitations:', error);
    throw error;
  }
};

/**
 * Deletes an invitation to the empresa
 * @param invitationDocumentId - The ID of the invitation to delete
 * @returns Promise indicating success or failure
 */
export const deleteEmpresaInvitation = async (
  invitationDocumentId: string
): Promise<void> => {
  try {
    const invitado = await ApiClient.get(`/user-invitations/${invitationDocumentId}`)

    await ApiClient.delete(`/empresa-usuarios/${invitado.data.data.documentId}`)
    await ApiClient.put(`/users/${invitado.data.data.id}`,{
      data:{
        blocked: true
      }
    })
    await ApiClient.delete(`/user-invitations/${invitationDocumentId}`);
  } catch (error) {
    console.error('Error deleting empresa invitation:', error);
    throw error;
  }
};

/**
 * Deletes a user from the system
 * @param documentId - The document ID of the user to delete
 * @returns Promise indicating success or failure
 */
export const deleteUser = async (
  documentId: string
): Promise<void> => {
  try {
    await ApiClient.delete(`/useradmin/${documentId}`);
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * Changes the role of a user
 * @param documentId - The document ID of the user whose role is to be changed
 * @param roleId - The ID of the new role
 * @returns Promise indicating success or failure
 */
export const changeUserRole = async (
  documentId: string,
  roleId: number | string
): Promise<void> => {
  try {
    const roleChangeData = {
      data: {
        roleId
      }
    };

    await ApiClient.put(`/useradmin/${documentId}/role`, roleChangeData);
  } catch (error) {
    console.error('Error changing user role:', error);
    throw error;
  }
};

/**
 * Blocks or unblocks a user
 * @param documentId - The document ID of the user to block/unblock
 * @param blocked - Whether the user should be blocked or not
 * @returns Promise indicating success or failure
 */
export const blockUser = async (
  documentId: string,
  blocked: boolean
): Promise<void> => {
  try {
    const blockData = {
      data: {
        blocked: blocked
      }
    };

    await ApiClient.put(`/useradmin/${documentId}/block`, blockData);
  } catch (error) {
    console.error('Error blocking/unblocking user:', error);
    throw error;
  }
};

/**
 * Fetches all available user roles
 * @returns Promise containing the list of available roles
 */
export const getRoles = async (): Promise<any> => {
  try {
    const response = await ApiClient.get('/users-permissions/roles');
    return response.data;
  } catch (error) {
    console.error('Error fetching roles:', error);
    throw error;
  }
};

/**
 * Fetches a user by their document ID
 * @param documentId - The document ID of the user to fetch
 * @returns Promise containing the user data
 */
export const getUserByDocumentId = async (
  documentId: string
): Promise<UsersPermissionsUser> => {
  try {
    const response = await ApiClient.get(`/useradmin/${documentId}`);
    return response.data.data; // Assuming the user data is in the data property of the response
  } catch (error) {
    console.error('Error fetching user by document ID:', error);
    throw error;
  }
};

export const getUserEmpresaByTokenInvitation = async (
  token: string
): Promise<UserInvitation | null> => {
  try {
    const response = await ApiClientOpen.get(`/user-invitations`, {
      params: {
        'filters[token][$eq]': token
      }
    });

    if(response.data.data.length === 0){
      return null;
    }

    return response.data.data[0];
  } catch (error) {
    console.error('Error fetching user empresa by token:', error);
    throw error;
  }
}

/**
 * Creates a new user with the invitation token
 * @param email - The email of the user
 * @param password - The password of the user
 * @param token - The invitation token
 * @returns Promise containing the created user
 */
export const createUserEmpresa = async (
  userData: {
    email: string,
    password: string,
    token: string
  }
): Promise<any> => {
  try {
    const invitacion = await getUserEmpresaByTokenInvitation(userData.token);

    if(!invitacion){
      throw new Error('Invitación no encontrada');
    }

    const response = await ApiClientOpen.post('/auth/local/register', {
      username: userData.email,
      email: userData.email,
      password: userData.password,
    });

    //crear el registro en empresa-usuarios se hace automaticamente en el backend
    const empresaUsuarioData = {
      usuario: response.data.user.id,
      empresa: invitacion.tenantId,
    };
    await ApiClient.post('empresa-usuarios', { data: empresaUsuarioData });

    //pasar el invitationToken a estado accepted tambien se hace automaticamente    
    await ApiClient.put(`/user-invitations/${invitacion.documentId}`, {
      data: {
        estado: 'accepted',
        userDocumentId: response.data.user.documentId
      }
    });

    if (response.status === 200) {         
      //asignar empresa al usuario en el store
      const empresaParaStore = {...response.data.user, empresa: invitacion.tenantId };
      useAuthStore.getState().setCredentials(response.data.jwt, empresaParaStore);
    } else {
      console.error('Error fetching auth callback:', response.statusText);
    }
    
    //si el usuario queda creado correctamente redirigir a dashboard
    window.location.href = '/dashboard';

    return response.data;
  } catch (error) {
    console.error('Error creating user empresa:', error);
    throw error;
  }
}

/**
 * Logs in a user with email and password
 * @param email - The email of the user
 * @param password - The password of the user
 * @returns Promise containing the login response
 */
export const loginUsuario = async (
  credentials: {
    email: string,
    password: string
  }
): Promise<any> => {
  try {
    const response = await ApiClientOpen.post('/auth/local', {
      identifier: credentials.email, // Using email as the identifier for Strapi
      password: credentials.password
    });

     if (response.status === 200) {
        //debo consultar empresa-usuario
        const EmpresaUsuario = await getEmpresaUsuarioByUsuarioId(response.data.user.id);
        //si la empresa no existe y si el usuarios es un superadmin entoncse
        //asignar empresa al usuario en el store
        const empresaParaStore = {...response.data.user, empresa: EmpresaUsuario?.empresa ?? null };
        useAuthStore.getState().setCredentials(response.data.jwt, empresaParaStore);
      } else {
        console.error('Error fetching auth callback:', response.statusText);
      }

    return response.data;
  } catch (error) {
    console.error('Error logging in user:', error);
    throw error;
  }
}

/*
este metodo es para crear un usuario administrador de cuenta con email y password 
 */
export const registerUsuario = async (email: string, password: string)=>{
  
    const data = {
      email, password, username: email
    }
    const usuario = await ApiClientOpen.post('auth/local/register', data);

    return usuario;

}

export const getEmpresaUsuarioByUsuarioId = async (usuarioId: number) =>{

    const response = await ApiClientOpen.get(`/empresa-usuarios`, {
      params: {
        'filters[usuario][$eq]': usuarioId
      }
    });

    if(response.data.data.length === 0){
      return null;
    }

    return response.data.data[0];

}

export const setState = async (state: string, provider: string)=>{

    await ApiClientOpen.post('user-tokens', {
      data:{
        refreshToken: "",
        accessToken: state,
        provider: provider,
        user: 0
      }
    })

}

/**
 * Requests a password reset for a user
 * @param email - The email of the user requesting password reset
 * @returns Promise indicating success or failure
 */
export const requestPasswordReset = async (
  email: string
): Promise<any> => {
  try {
    const response = await ApiClientOpen.post('/auth/forgot-password', {
      email
    });
    
    return response.data;
  } catch (error) {
    console.error('Error requesting password reset:', error);
    throw error;
  }
}

/**
 * Resets a user's password using the reset code
 * @param code - The reset code received via email
 * @param password - The new password
 * @param confirmPassword - The confirmation of the new password
 * @returns Promise containing the user data and JWT
 */
export const resetPassword = async (
  code: string,
  password: string,
  confirmPassword: string
): Promise<any> => {
  try {
    if (password !== confirmPassword) {
      throw new Error('Las contraseñas no coinciden.');
    }
    
    const response = await ApiClientOpen.post('/auth/reset-password', {
      code,
      password,
      passwordConfirmation: confirmPassword
    });
    
    // Update the auth store with the new credentials if successful
    const { jwt, user } = response.data;
    useAuthStore.getState().setCredentials(jwt, user);
    
    // Store the JWT token in a cookie (for API calls that might need it)
    document.cookie = `token=${jwt}; path=/; SameSite=Strict;`;
    
    return response.data;
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
}

